import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Empty, Input, Modal, Space, Table } from "antd";
import { api } from "./api/http";
import { useAuth } from "./auth/AuthProvider";
import {
  DashboardPrimaryActionButton,
  DashboardRefreshButton,
  DashboardSearchInput,
} from "./components/dashboard";
import { customerUserRoleLabel } from "./customerUserRoles";

const InviteWidget = forwardRef(function InviteWidget(
  {
    customerId,
    role,
    showContent = true,
    hideSectionHeading = false,
    searchTerm: controlledSearchTerm,
    onSearchTermChange,
    onRefreshReady,
    hideDesktopToolbar = false,
  },
  ref,
) {
  const { session } = useAuth();
  const [rows, setRows] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const searchTerm = controlledSearchTerm ?? internalSearchTerm;
  const setSearchTerm = onSearchTermChange ?? setInternalSearchTerm;
  const [notice, setNotice] = useState(null);
  const [inviteModalNotice, setInviteModalNotice] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedInviteKey, setSelectedInviteKey] = useState(null);

  const sendingRef = useRef(false);

  const userId = useMemo(() => {
    const fromSession = session?.userId ?? session?.userid ?? session?.id;
    const fromLS = Number(localStorage.getItem("userId") || 0);
    return Number(fromSession || fromLS || 0);
  }, [session]);

  const isCustomerMode = !!customerId;

  const load = useCallback(async () => {
    try {
      if (isCustomerMode) {
        const url = `/api/customers/${customerId}/invites${
          role ? `?role=${encodeURIComponent(role)}` : ""
        }`;
        const { data } = await api.get(url);
        setRows(Array.isArray(data) ? data : []);
      } else {
        if (!userId) {
          setRows([]);
          return;
        }
        const { data } = await api.get(`/api/users/${userId}/invites`);
        setRows(Array.isArray(data) ? data : []);
      }
    } catch {
      setRows([]);
    }
  }, [isCustomerMode, customerId, role, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onRefreshReady?.(load);
  }, [load, onRefreshReady]);

  const cancelInvite = async (row) => {
    if (!isCustomerMode) return;
    if (!window.confirm("Are you sure you want to cancel this invite?")) return;
    const id = row.invite_id ?? row.id;
    if (!id) return;
    try {
      await api.del(`/api/customers/${customerId}/invites/${id}`);
      await load();
    } catch {}
  };

  const resetInviteForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setConfirmEmail("");
  };

  const openInviteModal = useCallback(() => {
    setInviteModalNotice(null);
    setIsInviteModalOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openInviteModal,
    }),
    [openInviteModal],
  );

  const closeInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteModalNotice(null);
    resetInviteForm();
  };

  const sendInvite = async () => {
    if (!isCustomerMode) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedConfirmEmail = confirmEmail.trim();
    if (
      !trimmedFirst ||
      !trimmedLast ||
      !trimmedEmail ||
      !trimmedConfirmEmail
    ) {
      setInviteModalNotice({
        type: "err",
        text: "First name, last name, email, and confirm email are required.",
      });
      return;
    }
    if (trimmedEmail.toLowerCase() !== trimmedConfirmEmail.toLowerCase()) {
      setInviteModalNotice({
        type: "err",
        text: "Email addresses do not match.",
      });
      return;
    }

    if (sendingRef.current) return;
    sendingRef.current = true;
    setIsSending(true);
    setInviteModalNotice(null);

    try {
      const res = await api.post(`/api/customers/${customerId}/invites`, {
        email: trimmedEmail,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        role: role || "TECHNICIAN",
      });

      const d = res?.data ?? {};
      if (d?.warning || d?.email_sent === false) {
        setNotice({
          type: "warn",
          text: d?.warning || "Invite created but email failed to send.",
        });
      } else {
        setNotice({ type: "ok", text: "Invite created and email sent." });
      }

      resetInviteForm();
      setIsInviteModalOpen(false);
      await load();
    } catch (e) {
      const s = e?.status ?? e?.response?.status ?? 0;
      const m =
        e?.data?.message ?? e?.response?.data?.message ?? "Invite failed.";
      setInviteModalNotice({ type: "err", text: m });
      if (s === 202 || s === 409) {
        await load();
      }
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  const respond = async (row, decision) => {
    const inviteId = row.invite_id ?? row.id;
    if (!userId || !inviteId) return;
    try {
      await api.post(`/api/users/${userId}/invites/${inviteId}/respond`, {
        decision,
      });
      await load();
    } catch {}
  };

  const fmt = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) return String(ts);
    return date.toLocaleString();
  };

  const getInviteEmail = (row) => (row?.email || "").toLowerCase();

  const getInviteName = (row) => {
    const first = (row?.first_name ?? row?.firstName ?? "").trim();
    const last = (row?.last_name ?? row?.lastName ?? "").trim();
    const joinedName = [first, last].filter(Boolean).join(" ");
    return joinedName || (row?.full_name ?? row?.fullName ?? "").trim();
  };

  const getInviteTitle = (row) =>
    getInviteName(row) || getInviteEmail(row) || "Invite unavailable";

  const getInviteSentDate = (row) => fmt(row?.created_at ?? row?.createdAt);

  const roleLabel = customerUserRoleLabel(role || "TECHNICIAN");

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const emailVal = (r.email || "").toLowerCase();
      const nameVal = getInviteName(r).toLowerCase();
      const statusVal = (r.status_text || r.status || "").toLowerCase();
      const roleVal = (r.role || "").toLowerCase();
      return (
        emailVal.includes(q) ||
        nameVal.includes(q) ||
        statusVal.includes(q) ||
        roleVal.includes(q)
      );
    });
  }, [rows, searchTerm]);

  const getRowKey = (r) =>
    r.invite_id ?? r.id ?? `${r.email || ""}-${r.created_at || ""}`;

  useEffect(() => {
    if (
      selectedInviteKey &&
      !filteredRows.some((r) => getRowKey(r) === selectedInviteKey)
    ) {
      setSelectedInviteKey(null);
    }
  }, [filteredRows, selectedInviteKey]);

  const customerColumns = [
    {
      title: "Invitee",
      key: "invitee",
      render: (_, r) => (
        <div className="customer-users-invitee-cell">
          <strong>{getInviteTitle(r)}</strong>
          {getInviteName(r) && getInviteEmail(r) ? (
            <span>{getInviteEmail(r)}</span>
          ) : null}
        </div>
      ),
    },
    {
      title: "Sent",
      key: "sent",
      render: (_, r) => getInviteSentDate(r) || "—",
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, r) => (
        <div className="customer-users-table-action">
          <Button
            className="customer-users-remove-button"
            onClick={() => cancelInvite(r)}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  const userColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (text || "").toLowerCase(),
    },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Status",
      key: "status",
      render: (_, r) => r.status_text || "PENDING",
    },
    { title: "Invited", key: "invited", render: (_, r) => fmt(r.created_at) },
    {
      title: "Actions",
      key: "actions",
      width: 190,
      render: (_, r) => (
        <Space className="customer-users-invite-response-actions">
          <Button
            className="customer-users-accept-button"
            onClick={() => respond(r, "ACCEPT")}
          >
            Accept
          </Button>
          <Button
            className="customer-users-remove-button"
            onClick={() => respond(r, "DECLINE")}
          >
            Decline
          </Button>
        </Space>
      ),
    },
  ];

  const roleLower = roleLabel.toLowerCase();
  let inviteHelperText;
  if (roleLower === "technician") {
    inviteHelperText = "Invite technicians to connect their accounts.";
  } else if (roleLower === "operator") {
    inviteHelperText = "Invite operators to connect their accounts.";
  } else {
    inviteHelperText = `Invite ${roleLower}s to connect their accounts and access your forklifts and records.`;
  }

  const alertType =
    notice?.type === "err"
      ? "error"
      : notice?.type === "warn"
        ? "warning"
        : "success";

  return (
    <div className="customer-users-invite-widget">
      {isCustomerMode && (
        <div className="customer-users-mobile-page-action">
          <DashboardPrimaryActionButton
            htmlType="button"
            type="default"
            className="dashboard-mobile-page-action-button dashboard-secondary-action-button customer-users-mobile-invite-action"
            onClick={openInviteModal}
          >
            Invite {roleLabel}
          </DashboardPrimaryActionButton>
        </div>
      )}

      {showContent && (
        <>
          <div
            className={`customer-users-section-header customer-users-invite-section-header dashboard-fleet-table-toolbar maintenance-list-toolbar${hideSectionHeading ? " maintenance-list-toolbar-no-helper" : ""}${hideDesktopToolbar ? " customer-users-desktop-hidden-toolbar" : ""}`}
          >
            {!hideSectionHeading && (
              <div className="dashboard-fleet-table-toolbar-copy">
                <h2 className="customer-users-section-title">
                  Pending {roleLabel} Invites
                </h2>
                <p className="customer-users-section-helper">
                  {isCustomerMode
                    ? inviteHelperText
                    : "Review invitations awaiting your response."}
                </p>
              </div>
            )}
            <div className="customer-users-section-actions">
              <Space
                className="dashboard-fleet-table-controls customer-users-toolbar"
                wrap
              >
                <DashboardSearchInput
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search invites"
                  ariaLabel={`Search pending ${roleLabel.toLowerCase()} invites`}
                  className="dashboard-fleet-search maintenance-list-search customer-users-search"
                />
                <DashboardRefreshButton
                  onClick={load}
                  className="maintenance-list-refresh customer-users-refresh"
                />
              </Space>
            </div>
          </div>

          {notice && (
            <Alert
              className="customer-users-invite-alert"
              type={alertType}
              message={notice.text}
              showIcon
              closable
              onClose={() => setNotice(null)}
            />
          )}

          {isCustomerMode ? (
            <Table
              className="forklifts-page-table-wrap shared-dashboard-table customer-users-invite-table"
              dataSource={filteredRows}
              columns={customerColumns}
              rowKey={getRowKey}
              pagination={false}
              size="middle"
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No pending invites."
                  />
                ),
              }}
            />
          ) : (
            <Table
              className="forklifts-page-table-wrap shared-dashboard-table customer-users-invite-table"
              dataSource={filteredRows}
              columns={userColumns}
              rowKey={getRowKey}
              pagination={false}
              size="middle"
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No pending invites."
                  />
                ),
              }}
            />
          )}

          <div
            className="customer-users-mobile-list customer-users-invite-mobile-list"
            aria-label={`Pending ${roleLabel} invites`}
          >
            {filteredRows.length ? (
              filteredRows.map((r) => (
                <article
                  key={getRowKey(r)}
                  className={`customer-users-mobile-card shared-selected-card${getRowKey(r) === selectedInviteKey ? " is-selected" : ""}`}
                  onClick={() => setSelectedInviteKey(getRowKey(r))}
                >
                  {isCustomerMode ? (
                    <>
                      <div className="customer-users-mobile-invite-metadata">
                        <div className="customer-users-mobile-invite-label">
                          INVITE
                        </div>
                        <time>{getInviteSentDate(r) || "—"}</time>
                      </div>
                      <strong className="customer-users-mobile-invite-name">
                        {getInviteTitle(r)}
                      </strong>
                      {getInviteName(r) && getInviteEmail(r) ? (
                        <p className="customer-users-mobile-email">
                          {getInviteEmail(r)}
                        </p>
                      ) : null}
                      <div className="customer-users-mobile-invite-action-row">
                        <Button
                          className="customer-users-remove-button customer-users-mobile-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelInvite(r);
                          }}
                        >
                          Cancel Invite
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="customer-users-mobile-card-topline">
                        <strong>{getInviteTitle(r)}</strong>
                      </div>
                      {getInviteName(r) && getInviteEmail(r) ? (
                        <p className="customer-users-mobile-email">
                          {getInviteEmail(r)}
                        </p>
                      ) : null}
                      <dl className="customer-users-mobile-meta">
                        <div>
                          <dt>Sent</dt>
                          <dd>{getInviteSentDate(r) || "—"}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>{r.status_text || "PENDING"}</dd>
                        </div>
                      </dl>
                      <Space className="customer-users-invite-response-actions customer-users-mobile-invite-actions">
                        <Button
                          className="customer-users-accept-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            respond(r, "ACCEPT");
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          className="customer-users-remove-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            respond(r, "DECLINE");
                          }}
                        >
                          Decline
                        </Button>
                      </Space>
                    </>
                  )}
                </article>
              ))
            ) : (
              <Empty
                className="customer-users-mobile-empty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No pending invites."
              />
            )}
          </div>
        </>
      )}

      {isCustomerMode && (
        <Modal
          className="dashboard-form-modal customer-users-invite-modal"
          title={`Invite ${roleLabel}`}
          open={isInviteModalOpen}
          onCancel={closeInviteModal}
          footer={null}
          width={620}
          destroyOnClose
        >
          <p className="dashboard-modal-helper">
            Send a pending invitation for this {roleLabel.toLowerCase()} to
            connect their account.
          </p>
          {inviteModalNotice && (
            <Alert
              className="customer-users-invite-modal-alert"
              type={
                inviteModalNotice.type === "err"
                  ? "error"
                  : inviteModalNotice.type === "warn"
                    ? "warning"
                    : "success"
              }
              message={inviteModalNotice.text}
              showIcon
            />
          )}
          <form
            className="dashboard-modal-form-grid customer-users-invite-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
              sendInvite();
            }}
          >
            <label className="dashboard-modal-field">
              <span>First name</span>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </label>
            <label className="dashboard-modal-field">
              <span>Last name</span>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </label>
            <label className="dashboard-modal-field dashboard-modal-field-wide">
              <span>{roleLabel} email</span>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <label className="dashboard-modal-field dashboard-modal-field-wide">
              <span>Confirm {roleLabel.toLowerCase()} email</span>
              <Input
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="off"
              />
            </label>
            <div className="dashboard-modal-actions dashboard-modal-field-wide customer-users-invite-modal-actions">
              <Button
                className="dashboard-secondary-action-button"
                onClick={closeInviteModal}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="dashboard-primary-action-button"
                loading={isSending}
              >
                Send Invite
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
});

export default InviteWidget;
